import { io, Socket } from 'socket.io-client';
import { envConfig } from '../config/environment';

export type RemoteControlCommandType =
  | 'MOUSE_MOVE'
  | 'MOUSE_DOWN'
  | 'MOUSE_UP'
  | 'MOUSE_WHEEL'
  | 'KEY_DOWN'
  | 'KEY_UP'
  | 'CONTROL_PING'
  | 'CONTROL_STOP';

export interface ControlGrantedPayload {
  sessionId: string;
  controlToken: string;
  monitorId?: number | string;
  correlationId?: string;
  deviceId?: string;
}

export interface ControlDeniedPayload {
  reason?: string;
  message?: string;
  detail?: string;
}

export interface ControlEndedPayload {
  sessionId?: string;
  reason?: string;
}

export interface ControlErrorPayload {
  message?: string;
}

export interface ControlStatusPayload {
  deviceId?: string;
  windowsLocked?: boolean;
  secureDesktopDetected?: boolean;
  [key: string]: unknown;
}

export interface ControlCommandPayload {
  sessionId: string;
  controlToken: string;
  type: RemoteControlCommandType;
  commandId?: string;
  xRatio?: number;
  yRatio?: number;
  button?: number;
  delta?: number;
  keyCode?: number;
  virtualKey?: number;
  monitorId?: number | string;
}

/** Phase 2 — clipboard ack from broker (errors via ack, never auto-sync). */
export interface ClipboardAck {
  ok: boolean;
  text?: string;
  charCount?: number;
  byteCount?: number;
  error?: string;
  reason?: string;
  message?: string;
}

export interface ClipboardSendPayload {
  sessionId: string;
  controlToken: string;
  text: string;
}

export interface ClipboardGetPayload {
  sessionId: string;
  controlToken: string;
}

export type ChatSenderType = 'OPERATOR' | 'EMPLOYEE' | 'SYSTEM';

export interface ChatMessagePayload {
  messageId?: string;
  id?: string;
  sessionId?: string;
  from: ChatSenderType;
  text: string;
  at?: string | number;
  createdAt?: string | number;
}

export interface ChatHistoryPayload {
  sessionId?: string;
  messages?: ChatMessagePayload[];
}

export interface ChatSessionEndedPayload {
  sessionId?: string;
}

export interface ChatSendPayload {
  sessionId: string;
  controlToken: string;
  message: string;
}

export interface ChatSubscribePayload {
  sessionId: string;
  controlToken: string;
}

export interface ChatSendAck {
  ok?: boolean;
  success?: boolean;
  error?: string;
  reason?: string;
  message?: string;
  messageId?: string;
}

/** Phase 3 — viewer requests employee to pick a file for download. */
export interface FileDownloadRequestPayload {
  sessionId: string;
  controlToken: string;
}

export interface FileDownloadRequestAck {
  ok?: boolean;
  success?: boolean;
  transferRequestId?: string;
  error?: string;
  reason?: string;
  message?: string;
}

/** Server notifies viewer that a DOWNLOAD transfer is ready. */
export interface FileReadyPayload {
  sessionId?: string;
  transferId: string;
  fileName?: string;
  byteSize?: number;
  sha256?: string;
  status?: string;
}

export interface SupportActionPayload {
  sessionId: string;
  controlToken: string;
  actionId: string;
}

export interface SupportActionAck {
  ok?: boolean;
  success?: boolean;
  requestId?: string;
  error?: string;
  reason?: string;
  message?: string;
}

export interface SupportActionResultPayload {
  sessionId?: string;
  requestId?: string;
  actionId: string;
  ok: boolean;
  error?: string;
  resultSummary?: string;
  /** Present for COLLECT_DIAGNOSTICS — redacted recent agent.log lines. */
  recentLogLines?: string[];
}

type GrantedHandler = (payload: ControlGrantedPayload) => void;
type DeniedHandler = (payload: ControlDeniedPayload) => void;
type EndedHandler = (payload: ControlEndedPayload) => void;
type ErrorHandler = (payload: ControlErrorPayload) => void;
type StatusHandler = (payload: ControlStatusPayload) => void;
type DisconnectHandler = (reason?: string) => void;
type ChatMessageHandler = (payload: ChatMessagePayload) => void;
type ChatHistoryHandler = (payload: ChatHistoryPayload) => void;
type ChatSessionEndedHandler = (payload: ChatSessionEndedPayload) => void;
type FileReadyHandler = (payload: FileReadyPayload) => void;
type SupportActionResultHandler = (payload: SupportActionResultPayload) => void;

class RemoteControlSocketService {
  private socket: Socket | null = null;
  private connectPromise: Promise<void> | null = null;
  private grantedHandlers = new Set<GrantedHandler>();
  private deniedHandlers = new Set<DeniedHandler>();
  private endedHandlers = new Set<EndedHandler>();
  private errorHandlers = new Set<ErrorHandler>();
  private statusHandlers = new Set<StatusHandler>();
  private disconnectHandlers = new Set<DisconnectHandler>();
  private chatMessageHandlers = new Set<ChatMessageHandler>();
  private chatHistoryHandlers = new Set<ChatHistoryHandler>();
  private chatSessionEndedHandlers = new Set<ChatSessionEndedHandler>();
  private fileReadyHandlers = new Set<FileReadyHandler>();
  private supportActionResultHandlers = new Set<SupportActionResultHandler>();

  private getServerUrl(): string {
    return envConfig.apiUrl.startsWith('/')
      ? window.location.origin
      : envConfig.apiUrl.replace(/\/api$/, '');
  }

  get connected(): boolean {
    return Boolean(this.socket?.connected);
  }

  connect(): Promise<void> {
    if (this.socket?.connected) return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = new Promise<void>((resolve, reject) => {
      const token = localStorage.getItem('token');
      if (!token) {
        this.connectPromise = null;
        reject(new Error('Authentication required'));
        return;
      }

      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }

      this.socket = io(`${this.getServerUrl()}/remote-control`, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 8,
        auth: { token },
      });

      const onConnect = () => {
        cleanup();
        resolve();
      };
      const onError = (err: Error) => {
        cleanup();
        this.connectPromise = null;
        reject(err);
      };
      const cleanup = () => {
        this.socket?.off('connect', onConnect);
        this.socket?.off('connect_error', onError);
      };

      this.socket.on('connect', onConnect);
      this.socket.on('connect_error', onError);

      this.socket.on('control_granted', (payload: ControlGrantedPayload) => {
        this.grantedHandlers.forEach((handler) => handler(payload));
      });
      this.socket.on('control_denied', (payload: ControlDeniedPayload) => {
        this.deniedHandlers.forEach((handler) => handler(payload));
      });
      this.socket.on('control_ended', (payload: ControlEndedPayload) => {
        this.endedHandlers.forEach((handler) => handler(payload));
      });
      this.socket.on('control_error', (payload: ControlErrorPayload) => {
        this.errorHandlers.forEach((handler) => handler(payload));
      });
      this.socket.on('control_status', (payload: ControlStatusPayload) => {
        this.statusHandlers.forEach((handler) => handler(payload));
      });
      this.socket.on('chat_message', (payload: ChatMessagePayload) => {
        this.chatMessageHandlers.forEach((handler) => handler(payload));
      });
      this.socket.on('chat_history', (payload: ChatHistoryPayload) => {
        this.chatHistoryHandlers.forEach((handler) => handler(payload));
      });
      this.socket.on('chat_session_ended', (payload: ChatSessionEndedPayload) => {
        this.chatSessionEndedHandlers.forEach((handler) => handler(payload));
      });
      this.socket.on('file_ready', (payload: FileReadyPayload) => {
        this.fileReadyHandlers.forEach((handler) => handler(payload));
      });
      this.socket.on('support_action_result', (payload: SupportActionResultPayload) => {
        this.supportActionResultHandlers.forEach((handler) => handler(payload));
      });
      this.socket.on('disconnect', (reason: string) => {
        this.disconnectHandlers.forEach((handler) => handler(reason));
      });
    });

    return this.connectPromise.finally(() => {
      this.connectPromise = null;
    });
  }

  startControl(
    payload: { deviceId: string; monitorId?: number | string; correlationId?: string },
  ): Promise<ControlGrantedPayload> {
    if (!this.socket?.connected) {
      return Promise.reject(new Error('Remote Control socket not connected'));
    }
    return new Promise((resolve, reject) => {
      this.socket!.emit('control_start', payload, (ack: any) => {
        if (ack?.success && ack?.data) {
          resolve(ack.data as ControlGrantedPayload);
          return;
        }
        reject(new Error(ack?.message || ack?.reason || 'Control start denied'));
      });
    });
  }

  stopControl(payload: { sessionId: string; controlToken: string }): Promise<void> {
    if (!this.socket?.connected) return Promise.resolve();
    return new Promise((resolve) => {
      this.socket!.emit('control_stop', payload, () => resolve());
      setTimeout(() => resolve(), 1500);
    });
  }

  sendCommand(payload: ControlCommandPayload): void {
    if (!this.socket?.connected) return;
    this.socket.emit('control_command', payload);
  }

  ping(payload: { sessionId: string; controlToken: string }): void {
    if (!this.socket?.connected) return;
    this.socket.emit('control_ping', payload);
  }

  sendClipboardToRemote(payload: ClipboardSendPayload): Promise<ClipboardAck> {
    if (!this.socket?.connected) {
      return Promise.resolve({ ok: false, error: 'Remote Control socket not connected', reason: 'DISCONNECTED' });
    }
    return new Promise((resolve) => {
      this.socket!.emit('clipboard_send_to_remote', payload, (ack: ClipboardAck) => {
        resolve(ack || { ok: false, error: 'No acknowledgment' });
      });
    });
  }

  getClipboardFromRemote(payload: ClipboardGetPayload): Promise<ClipboardAck> {
    if (!this.socket?.connected) {
      return Promise.resolve({ ok: false, error: 'Remote Control socket not connected', reason: 'DISCONNECTED' });
    }
    return new Promise((resolve) => {
      this.socket!.emit('clipboard_get_from_remote', payload, (ack: ClipboardAck) => {
        resolve(ack || { ok: false, error: 'No acknowledgment' });
      });
    });
  }

  sendChatMessage(payload: ChatSendPayload): Promise<ChatSendAck> {
    if (!this.socket?.connected) {
      return Promise.reject(new Error('Remote Control socket not connected'));
    }
    return new Promise((resolve, reject) => {
      this.socket!.emit('chat_send', payload, (ack: ChatSendAck) => {
        if (ack?.ok === false || ack?.success === false) {
          reject(new Error(ack?.message || ack?.error || ack?.reason || 'Chat send failed'));
          return;
        }
        resolve(ack || { ok: true });
      });
    });
  }

  subscribeChat(payload: ChatSubscribePayload): void {
    if (!this.socket?.connected) return;
    this.socket.emit('chat_subscribe', payload);
  }

  requestFileDownload(payload: FileDownloadRequestPayload): Promise<FileDownloadRequestAck> {
    if (!this.socket?.connected) {
      return Promise.resolve({
        ok: false,
        error: 'Remote Control socket not connected',
        reason: 'DISCONNECTED',
      });
    }
    return new Promise((resolve) => {
      this.socket!.emit('file_download_request', payload, (ack: FileDownloadRequestAck) => {
        resolve(ack || { ok: false, error: 'No acknowledgment' });
      });
    });
  }

  executeSupportAction(payload: SupportActionPayload): Promise<SupportActionAck> {
    if (!this.socket?.connected) {
      return Promise.resolve({
        ok: false,
        error: 'Remote Control socket not connected',
        reason: 'DISCONNECTED',
      });
    }
    return new Promise((resolve) => {
      this.socket!.emit('support_action', payload, (ack: SupportActionAck) => {
        resolve(ack || { ok: false, error: 'No acknowledgment' });
      });
    });
  }

  onGranted(handler: GrantedHandler): () => void {
    this.grantedHandlers.add(handler);
    return () => this.grantedHandlers.delete(handler);
  }

  onDenied(handler: DeniedHandler): () => void {
    this.deniedHandlers.add(handler);
    return () => this.deniedHandlers.delete(handler);
  }

  onEnded(handler: EndedHandler): () => void {
    this.endedHandlers.add(handler);
    return () => this.endedHandlers.delete(handler);
  }

  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  onDisconnect(handler: DisconnectHandler): () => void {
    this.disconnectHandlers.add(handler);
    return () => this.disconnectHandlers.delete(handler);
  }

  onChatMessage(handler: ChatMessageHandler): () => void {
    this.chatMessageHandlers.add(handler);
    return () => this.chatMessageHandlers.delete(handler);
  }

  onChatHistory(handler: ChatHistoryHandler): () => void {
    this.chatHistoryHandlers.add(handler);
    return () => this.chatHistoryHandlers.delete(handler);
  }

  onChatSessionEnded(handler: ChatSessionEndedHandler): () => void {
    this.chatSessionEndedHandlers.add(handler);
    return () => this.chatSessionEndedHandlers.delete(handler);
  }

  onFileReady(handler: FileReadyHandler): () => void {
    this.fileReadyHandlers.add(handler);
    return () => this.fileReadyHandlers.delete(handler);
  }

  onSupportActionResult(handler: SupportActionResultHandler): () => void {
    this.supportActionResultHandlers.add(handler);
    return () => this.supportActionResultHandlers.delete(handler);
  }

  disconnect(): void {
    this.grantedHandlers.clear();
    this.deniedHandlers.clear();
    this.endedHandlers.clear();
    this.errorHandlers.clear();
    this.statusHandlers.clear();
    this.disconnectHandlers.clear();
    this.chatMessageHandlers.clear();
    this.chatHistoryHandlers.clear();
    this.chatSessionEndedHandlers.clear();
    this.fileReadyHandlers.clear();
    this.supportActionResultHandlers.clear();
    this.connectPromise = null;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

const remoteControlSocket = new RemoteControlSocketService();
export default remoteControlSocket;
